import type { FC, Ref } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui';
import { extractNameFromEmail } from '@shared/utils/formatters.utils';

import type { Investigation } from '../../../api/files.types';
import { transformDataForTable } from '../investigation-report/investigationReport.utils';

dayjs.extend(utc);

const formattedDate = (date: string | null) => {
  if (!date) return 'N/A';
  return dayjs(date).utc().format('MMM D, YYYY, h:mm:ss A [UTC]');
};

interface InvestigationReportTemplateProps {
  investigation: Investigation;
  ref?: Ref<HTMLDivElement>;
}

export const InvestigationReportTemplate: FC<InvestigationReportTemplateProps> = ({ investigation, ref }) => {
  const eventSources = transformDataForTable(investigation);
  const { traceName, generatedAt, outcome, assets, id, status, runBy, runDate, dateTo, dateFrom, scope } =
    investigation?.data || {};

  return (
    <div className="max-w-4xl mx-auto space-y-12 pb-20" data-testid="investigation-report-content" ref={ref}>
      <div className="space-y-2" data-testid="investigation-report-header">
        <div className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">Investigation Report</div>
        <h1 className="text-3xl font-normal text-foreground" data-testid="investigation-report-title">
          {traceName}
        </h1>
        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
          <span>
            Generated:{' '}
            <span className="text-foreground" data-testid="investigation-report-generated-date">
              {formattedDate(generatedAt)}
            </span>
          </span>
        </div>
      </div>

      <div
        className="bg-muted/50 border border-border rounded-lg p-6"
        data-testid="investigation-report-finding-status"
      >
        <div className="flex items-center gap-2 mb-2">
          <span className="text-muted-foreground text-sm">Finding Status:</span>
          <span className="text-foreground font-semibold">No Activities Detected</span>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed">
          The analysis of {outcome?.totalRawProcessed?.toLocaleString() ?? 0} events across {assets?.length ?? 0} SAP
          systems within the specified time range yielded no activities matching the investigation parameters.
        </p>
      </div>

      <section className="space-y-6" data-testid="investigation-report-summary-section">
        <h3 className="text-lg font-medium text-muted-foreground uppercase tracking-wide border-b border-border pb-2">
          Investigation Summary
        </h3>

        <div className="grid grid-cols-1 gap-y-4 text-sm">
          <div className="grid grid-cols-[140px_1fr]">
            <span className="text-muted-foreground">Investigation Name:</span>
            <span className="text-foreground font-mono" data-testid="investigation-report-trace-name">
              {traceName}
            </span>
          </div>
          <div className="grid grid-cols-[140px_1fr]">
            <span className="text-muted-foreground">Investigation ID:</span>
            <span className="text-foreground font-mono" data-testid="investigation-report-id">
              {id}
            </span>
          </div>

          <div className="grid grid-cols-[140px_1fr]">
            <span className="text-muted-foreground">Status:</span>
            <span className="text-foreground" data-testid="investigation-report-status">
              {status}
            </span>
          </div>

          <div className="grid grid-cols-[140px_1fr]">
            <span className="text-muted-foreground">Investigator:</span>
            <span className="text-foreground" data-testid="investigation-report-investigator">
              {extractNameFromEmail(runBy)}
            </span>
          </div>

          <div className="grid grid-cols-[140px_1fr]">
            <span className="text-muted-foreground">Investigation Date:</span>
            <span className="text-foreground" data-testid="investigation-report-date">
              {formattedDate(runDate)}
            </span>
          </div>
        </div>
      </section>

      <section className="space-y-6" data-testid="investigation-report-criteria-section">
        <h3 className="text-lg font-medium text-muted-foreground uppercase tracking-wide border-b border-border pb-2">
          Investigation Criteria
        </h3>
        <div className="grid grid-cols-1 gap-y-4 text-sm">
          <div className="grid grid-cols-[140px_1fr]">
            <span className="text-muted-foreground">Time Range:</span>
            <div className="text-foreground" data-testid="investigation-report-time-range">
              <div>Start: {formattedDate(dateFrom)}</div>
              <div>End: {formattedDate(dateTo)}</div>
            </div>
          </div>
          <div className="grid grid-cols-[140px_1fr]">
            <span className="text-muted-foreground">Scope:</span>
            <span className="text-foreground" data-testid="investigation-report-scope">
              User: {scope?.userName ?? ' -- '}
            </span>
          </div>
          <div className="grid grid-cols-[140px_1fr]">
            <span className="text-muted-foreground">Events Processed:</span>
            <span className="text-foreground" data-testid="investigation-report-events-processed">
              {outcome?.totalRawProcessed?.toLocaleString() ?? 0}
            </span>
          </div>
        </div>
      </section>

      {/* III. Application Instances */}
      <section
        className="space-y-6 print-keep-together"
        data-testid="investigation-report-application-instances-section"
      >
        <h3 className="text-lg font-medium text-muted-foreground uppercase tracking-wide border-b border-border pb-2">
          Application Instances
        </h3>
        <div className="text-sm text-muted-foreground mb-4" data-testid="investigation-report-total-systems">
          Total Systems: {assets?.length ?? 0}
        </div>

        <div
          className="border border-border rounded-lg overflow-hidden bg-card/30"
          data-testid="investigation-report-application-instances-table-wrapper"
        >
          <Table data-testid="investigation-report-application-instances-table">
            <TableHeader className="bg-muted/50">
              <TableRow className="border-border hover:bg-muted/50">
                <TableHead className="text-xs font-bold text-muted-foreground uppercase h-10">System ID</TableHead>
                <TableHead className="text-xs font-bold text-muted-foreground uppercase h-10">Type</TableHead>
                <TableHead className="text-xs font-bold text-muted-foreground uppercase h-10 text-right">
                  Events
                </TableHead>
                <TableHead className="text-xs font-bold text-muted-foreground uppercase h-10">Hostname</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eventSources.map((row, index) => (
                <TableRow
                  key={index}
                  className="border-border even:bg-muted/40 hover:bg-transparent hover:even:bg-muted/40"
                  data-testid={`investigation-report-application-instances-row-${index}`}
                >
                  <TableCell
                    className="font-medium text-foreground"
                    data-testid={`investigation-report-application-instances-sid-${index}`}
                  >
                    {row.sid}
                  </TableCell>
                  <TableCell data-testid={`investigation-report-application-instances-type-${index}`}>
                    <Badge
                      variant="secondary"
                      className="bg-muted text-muted-foreground rounded text-[10px] px-1.5 py-0"
                    >
                      {row.type}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className="text-right text-foreground font-mono"
                    data-testid={`investigation-report-application-instances-events-${index}`}
                  >
                    {row.eventsTotal}
                  </TableCell>
                  <TableCell
                    className="text-muted-foreground font-mono text-xs"
                    data-testid={`investigation-report-application-instances-hostname-${index}`}
                  >
                    {row.hostname}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* V. Certification */}
      <section className="space-y-6 pt-8" data-testid="investigation-report-certification-section">
        <div className="text-lg font-medium text-muted-foreground uppercase tracking-wide border-b border-border pb-2" />
        <p
          className="text-muted-foreground text-sm leading-relaxed"
          data-testid="investigation-report-certification-text"
        >
          Onapsis®, the Onapsis logo, and Onapsis Investigate™ are trademarks of Onapsis, Inc. All rights reserved. All
          other trademarks are property of their respective owners. Removing or attempting to remove any proprietary
          notices or labels, or the reverse engineering, disassembly, or decompilation of the products, except to the
          extent required to obtain interoperability with other independently created software or as specified by law,
          is prohibited. The products (which include both the software and documentation) contain proprietary
          information; they are provided under a license agreement containing restrictions on use and disclosure and are
          also protected by copyright, patent, and other intellectual and industrial property laws. Except as expressly
          provided in any written license agreement from Onapsis, the furnishing of this document does not give you any
          license to or under these patents, trademarks, copyrights, or other intellectual property. Onapsis will have
          no liability with respect to the use or reliance upon this data by the recipient. Any findings, assessments,
          or observations presented in this report are based solely on the data, information, and system access made
          available to the products at the time of the investigation and preparation of this report, and Onapsis makes
          no representations or warranties, express or implied regarding the completeness or accuracy thereof or that
          this document is error-free. Except as may be expressly permitted in your license agreement for these
          products, no part of these products may be reproduced or transmitted in any form or by any means, electronic
          or mechanical, for any purpose.
        </p>
      </section>
    </div>
  );
};
